<template>
  <div class="SubscriptionCycle">
    <p v-if="title" class="SubscriptionCycle-title">
      {{ title }}
    </p>

    <Timeline
      v-if="isMobileOrTablet"
      class="SubscriptionCycle-vertical"
      :sections="timelineSections"
      itemKey="title"
    >
      <template #marker="{ index }">
        <Icon :icon="steps[index].icon" size="md" />
      </template>

      <template #item="{ item }">
        <span v-if="item.description" class="SubscriptionCycle-text">
          {{ item.description }}
        </span>
      </template>
    </Timeline>

    <ol v-else class="SubscriptionCycle-steps">
      <li
        v-for="step of steps"
        :key="step.title"
        class="SubscriptionCycle-step"
      >
        <span class="SubscriptionCycle-marker">
          <Icon :icon="step.icon" size="md" />
        </span>

        <span class="SubscriptionCycle-label">{{ step.title }}</span>

        <span v-if="step.description" class="SubscriptionCycle-text">
          {{ step.description }}
        </span>
      </li>
    </ol>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Icon from '@cvo/ui/components/atoms/Icon/Icon.vue';
import Timeline from '@cvo/ui/components/organisms/Timeline/Timeline.vue';
import { useResponsive } from '@cvo/ui/composables';
import type { SubscriptionCycleProps } from './SubscriptionCycle.model';

const props = defineProps<SubscriptionCycleProps>();

const { isMobileOrTablet } = useResponsive();

const timelineSections = computed(() => props.steps.map(step => ({
  label: step.title,
  items: [step],
})));
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

$cycle-marker-size: vars.$dimensions-d-8;

@layer cvo-ui {
  .SubscriptionCycle {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: vars.$dimensions-d-3;
    padding-block: vars.$dimensions-d-4;

    @include responsive.desktop {
      gap: vars.$dimensions-d-8;
    }

    &-title {
      margin: 0;
      font-weight: vars.$typography-weight-strong;
      color: vars.$organisms-timeline-subscriptioncycle-title-color;
      text-align: center;

      @include text.text-responsive('sm', 'sm', 'md');
    }

    &-vertical {
      align-self: stretch;
      padding-inline-start: vars.$dimensions-d-4;

      .Timeline {
        &-marker,
        &-markerBullet {
          inline-size: $cycle-marker-size;
        }

        &-markerBullet {
          block-size: $cycle-marker-size;
          color: vars.$organisms-timeline-event-secondary-icon-color;
        }

        &-section {
          gap: vars.$dimensions-d-2;
        }

        &-sectionContent {
          padding-block-end: vars.$dimensions-d-4;
        }

        &-label {
          @include text.text('xs', vars.$typography-weight-strong);
          color: vars.$organisms-timeline-subscriptioncycle-text-color;
        }

        &-item {
          padding-block-start: vars.$dimensions-d-0-5;
        }
      }
    }

    &-steps {
      display: flex;
      inline-size: 100%;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    &-step {
      position: relative;
      display: flex;
      flex: 1 1 0;
      min-inline-size: 0;
      flex-direction: column;
      align-items: center;
      text-align: center;

      // Connector reaching the next marker centre. Equal-width steps make
      // `50%` the marker centre and `100%` the centre-to-centre distance.
      // `d-3-5` centres it on the marker: (marker - connector) / 2. Note this
      // anchors on the marker, unlike HowItWorks' card connector which centres
      // on the card — the two designs differ, so don't harmonise them.
      &:not(:last-child)::after {
        content: '';
        position: absolute;
        inset-inline-start: 50%;
        inset-block-start: vars.$dimensions-d-3-5;
        inline-size: 100%;
        block-size: vars.$dimensions-d-1;
        background-color: vars.$organisms-timeline-event-secondary-bg-color;
      }
    }

    &-marker {
      @include layout.square($cycle-marker-size);
      // Lifts the marker above the connector, which is painted by the step's
      // ::after and would otherwise cross the icon
      position: relative;
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin-block-end: vars.$dimensions-d-3;
      color: vars.$organisms-timeline-event-secondary-icon-color;
      background-color: vars.$organisms-timeline-event-secondary-bg-color;
      border-radius: vars.$border-radius-full;
    }

    &-label {
      @include text.text('sm', vars.$typography-weight-strong);
      color: vars.$organisms-timeline-subscriptioncycle-text-color;
    }

    &-text {
      color: vars.$organisms-timeline-subscriptioncycle-secondary-text-color;

      @include text.text-responsive('xs', 'xs', 'sm');
    }

    // Our line boxes run 2px taller than Figma's, so the tighter gap is what
    // keeps the two lines reading as one block centred under the icon
    &-steps &-text {
      margin-block-start: vars.$dimensions-d-0-5;
    }
  }
}
</style>
